/**
 * Admin extensions: Resources (PDF upload), Blog, Leadership, Program Status,
 * Enrollments, Partnerships.
 */
import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Upload, Edit3, Save, X, Eye, EyeOff, Download, FileText } from "lucide-react";
import {
  adminListResources, adminUploadResource, adminUpdateResource, adminDeleteResource,
  adminReplaceResource, adminResourceDownloadUrl,
  adminListBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog,
  listLeadership, adminCreateLeader, adminUpdateLeader, adminDeleteLeader,
  getProgramStatus, adminUpdateProgramStatus,
  adminEnrollments, adminUpdateEnrollment,
  adminPartnerships, adminUpdatePartnership,
} from "../lib/api";
import { SIGNATURE_PROGRAMS, STATUS_LABELS } from "../lib/signaturePrograms";

const RES_CATEGORIES = [
  "Parent Guides", "Youth Resources", "Program Brochures",
  "Mental Wellness", "Drug Prevention", "School Partnerships",
  "Sponsorship Packets", "Facilitator Manuals", "Other",
];

export function ResourcesAdmin() {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Parent Guides", visibility: "public", file: null });
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-resources"], queryFn: adminListResources });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Please choose a file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title || form.file.name);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("visibility", form.visibility);
      await adminUploadResource(fd);
      toast.success("Uploaded.");
      setForm({ title: "", description: "", category: "Parent Guides", visibility: "public", file: null });
      e.target.reset();
      qc.invalidateQueries({ queryKey: ["admin-resources"] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleVis = async (r) => {
    try {
      await adminUpdateResource(r.id, { visibility: r.visibility === "public" ? "restricted" : "public" });
      qc.invalidateQueries({ queryKey: ["admin-resources"] });
      toast.success(`Marked ${r.visibility === "public" ? "restricted" : "public"}`);
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this resource?")) return;
    try { await adminDeleteResource(id); qc.invalidateQueries({ queryKey: ["admin-resources"] }); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const replace = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await adminReplaceResource(id, fd); qc.invalidateQueries({ queryKey: ["admin-resources"] }); toast.success("File replaced"); }
    catch (err) { toast.error(err.response?.data?.detail || "Replace failed"); }
  };

  return (
    <div className="grid gap-6">
      <form onSubmit={submit} className="rounded-lg bg-white border border-black/10 p-6 grid gap-3" data-testid="admin-resource-upload">
        <h3 className="font-display text-xl font-bold">Upload a PDF resource</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="res-upload-title" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="res-upload-cat">
            {RES_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="res-upload-vis">
            <option value="public">Public — anyone can download</option>
            <option value="restricted">Restricted — admins only</option>
          </select>
          <input type="file" accept=".pdf,application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="text-sm" data-testid="res-upload-file" required />
        </div>
        <textarea placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
        <button disabled={uploading} className="btn-primary w-fit" data-testid="res-upload-submit">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
        </button>
      </form>

      <div className="rounded-lg bg-white border border-black/10 overflow-hidden">
        <div className="p-4 border-b border-black/5"><h3 className="font-display text-xl font-bold">Library ({data.length})</h3></div>
        {isLoading ? <p className="p-6 text-sm text-black/60">Loading…</p> : data.length === 0 ? (
          <p className="p-6 text-sm text-black/60">No resources yet. Upload your first PDF above.</p>
        ) : (
          <div className="divide-y divide-black/5">
            {data.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-start gap-4 justify-between" data-testid={`admin-resource-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-4 w-4 text-[#6B21A8]" />
                    <p className="font-semibold">{r.title}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.visibility === "public" ? "bg-emerald-100 text-emerald-800" : "bg-black/10 text-black/70"}`}>
                      {r.visibility}
                    </span>
                    <span className="text-xs bg-[#6B21A8]/10 text-[#6B21A8] px-2 py-0.5 rounded-full">{r.category}</span>
                  </div>
                  {r.description && <p className="mt-1 text-sm text-black/60">{r.description}</p>}
                  <p className="mt-1 text-xs text-black/40">{r.original_filename} · {(r.size / 1024).toFixed(0)} KB</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a href={adminResourceDownloadUrl(r.id)} target="_blank" rel="noreferrer" className="rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-black/5 inline-flex items-center gap-1" data-testid={`res-view-${r.id}`}><Download className="h-3 w-3" /> View</a>
                  <button onClick={() => toggleVis(r)} className="rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-black/5 inline-flex items-center gap-1" data-testid={`res-toggle-${r.id}`}>
                    {r.visibility === "public" ? <><EyeOff className="h-3 w-3" /> Make restricted</> : <><Eye className="h-3 w-3" /> Make public</>}
                  </button>
                  <label className="rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-black/5 cursor-pointer inline-flex items-center gap-1">
                    <Edit3 className="h-3 w-3" /> Replace
                    <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && replace(r.id, e.target.files[0])} data-testid={`res-replace-${r.id}`} />
                  </label>
                  <button onClick={() => del(r.id)} className="rounded-md border border-red-300 text-red-700 px-3 py-1.5 text-xs font-semibold hover:bg-red-50 inline-flex items-center gap-1" data-testid={`res-delete-${r.id}`}><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const BLOG_CATEGORIES = [
  "Mentoring Matters", "Youth Mental Wellness", "Drug Prevention Awareness",
  "Parenting and Families", "Leadership and Life Skills",
  "Community Impact", "Events and Announcements",
];

export function BlogAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: posts = [] } = useQuery({ queryKey: ["admin-blog"], queryFn: adminListBlog });

  const save = async () => {
    if (!editing.title || !editing.body) { toast.error("Title and body required."); return; }
    try {
      if (editing.id) {
        await adminUpdateBlog(editing.id, editing);
        toast.success("Updated");
      } else {
        await adminCreateBlog(editing);
        toast.success("Created");
      }
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this post?")) return;
    try { await adminDeleteBlog(id); qc.invalidateQueries({ queryKey: ["admin-blog"] }); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="grid gap-6">
      {!editing && (
        <button onClick={() => setEditing({ title: "", body: "", excerpt: "", category: "Mentoring Matters", status: "draft", cover_image: "" })} className="btn-primary w-fit" data-testid="blog-new">
          <Plus className="h-4 w-4" /> New Post
        </button>
      )}
      {editing && (
        <div className="rounded-lg bg-white border border-black/10 p-6 grid gap-3" data-testid="blog-editor">
          <h3 className="font-display text-xl font-bold">{editing.id ? "Edit Post" : "New Post"}</h3>
          <input placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="blog-title" />
          <input placeholder="Excerpt (short preview)" value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
          <div className="grid gap-3 md:grid-cols-2">
            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="blog-cat">
              {BLOG_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="blog-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <input placeholder="Cover image URL (optional)" value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
          <textarea placeholder="Body (plain text or Markdown-ish — preserved line breaks)" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={12} className="rounded-md border border-black/10 px-3 py-2 text-sm font-mono" data-testid="blog-body" />
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary" data-testid="blog-save"><Save className="h-4 w-4" /> Save</button>
            <button onClick={() => setEditing(null)} className="btn-outline" data-testid="blog-cancel"><X className="h-4 w-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-white border border-black/10 overflow-hidden">
        <div className="p-4 border-b border-black/5"><h3 className="font-display text-xl font-bold">Posts ({posts.length})</h3></div>
        {posts.length === 0 ? <p className="p-6 text-sm text-black/60">No posts yet.</p> : (
          <div className="divide-y divide-black/5">
            {posts.map((p) => (
              <div key={p.id} className="p-4 flex items-start gap-4 justify-between" data-testid={`blog-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{p.title}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{p.status}</span>
                    <span className="text-xs bg-[#6B21A8]/10 text-[#6B21A8] px-2 py-0.5 rounded-full">{p.category}</span>
                  </div>
                  {p.excerpt && <p className="mt-1 text-sm text-black/60">{p.excerpt}</p>}
                  <p className="mt-1 text-xs text-black/40">/{p.slug} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(p)} className="rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold" data-testid={`blog-edit-${p.id}`}><Edit3 className="h-3 w-3 inline" /> Edit</button>
                  <button onClick={() => del(p.id)} className="rounded-md border border-red-300 text-red-700 px-3 py-1.5 text-xs font-semibold" data-testid={`blog-delete-${p.id}`}><Trash2 className="h-3 w-3 inline" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function LeadershipAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["leadership"], queryFn: listLeadership });

  const save = async () => {
    if (!editing.name || !editing.role) { toast.error("Name and role required."); return; }
    try {
      if (editing.id) await adminUpdateLeader(editing.id, editing);
      else await adminCreateLeader(editing);
      toast.success("Saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["leadership"] });
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this team member?")) return;
    try { await adminDeleteLeader(id); qc.invalidateQueries({ queryKey: ["leadership"] }); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="grid gap-6">
      {!editing && <button onClick={() => setEditing({ name: "", role: "", bio: "", photo: "", order: 100 })} className="btn-primary w-fit" data-testid="leader-new"><Plus className="h-4 w-4" /> Add Team Member</button>}
      {editing && (
        <div className="rounded-lg bg-white border border-black/10 p-6 grid gap-3" data-testid="leader-editor">
          <h3 className="font-display text-xl font-bold">{editing.id ? "Edit" : "Add"} Team Member</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="leader-name" />
            <input placeholder="Role / Title" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="leader-role" />
            <input placeholder="Photo URL (optional)" value={editing.photo || ""} onChange={(e) => setEditing({ ...editing, photo: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
            <input type="number" placeholder="Display order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
          </div>
          <textarea placeholder="Short bio" value={editing.bio || ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} rows={3} className="rounded-md border border-black/10 px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Save</button>
            <button onClick={() => setEditing(null)} className="btn-outline"><X className="h-4 w-4" /> Cancel</button>
          </div>
        </div>
      )}
      <div className="rounded-lg bg-white border border-black/10 overflow-hidden">
        {data.length === 0 ? <p className="p-6 text-sm text-black/60">No team members yet.</p> : (
          <div className="divide-y divide-black/5">
            {data.map((l) => (
              <div key={l.id} className="p-4 flex items-center gap-4 justify-between">
                <div><p className="font-semibold">{l.name}</p><p className="text-sm text-[#6B21A8]">{l.role}</p>{l.bio && <p className="text-xs text-black/60 mt-1">{l.bio}</p>}</div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(l)} className="rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold">Edit</button>
                  <button onClick={() => del(l.id)} className="rounded-md border border-red-300 text-red-700 px-3 py-1.5 text-xs font-semibold">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ProgramStatusAdmin() {
  const qc = useQueryClient();
  const { data: statusMap = {} } = useQuery({ queryKey: ["program-status"], queryFn: getProgramStatus });
  const [local, setLocal] = useState({});
  const effective = { ...statusMap, ...local };

  const save = async () => {
    try {
      await adminUpdateProgramStatus(local);
      toast.success("Program status updated");
      setLocal({});
      qc.invalidateQueries({ queryKey: ["program-status"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid gap-4">
      <p className="text-sm text-black/60">Set the public status label for each program. Changes publish instantly to the /programs page.</p>
      <div className="rounded-lg bg-white border border-black/10 overflow-hidden">
        <div className="divide-y divide-black/5">
          {SIGNATURE_PROGRAMS.map((p) => (
            <div key={p.slug} className="p-4 flex items-center justify-between gap-4" data-testid={`prog-status-row-${p.slug}`}>
              <div>
                <p className="font-semibold">{p.letter}. {p.title}</p>
                <p className="text-xs text-black/50">Ages {p.ages}{p.duration ? ` · ${p.duration}` : ""}</p>
              </div>
              <select value={effective[p.slug] || "operating"} onChange={(e) => setLocal({ ...local, [p.slug]: e.target.value })} className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid={`prog-status-${p.slug}`}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={Object.keys(local).length === 0} className="btn-primary w-fit" data-testid="prog-status-save">
        <Save className="h-4 w-4" /> Save Changes
      </button>
    </div>
  );
}

export function EnrollmentsAdmin() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("new");
  const { data = [] } = useQuery({ queryKey: ["admin-enrollments", status], queryFn: () => adminEnrollments(status || null) });
  const update = async (id, s) => {
    try { await adminUpdateEnrollment(id, s); toast.success(`Marked ${s}`); qc.invalidateQueries({ queryKey: ["admin-enrollments"] }); }
    catch { toast.error("Failed"); }
  };
  return (
    <>
      <div className="mb-4 flex gap-2 flex-wrap">
        {["new", "in_review", "enrolled", "waitlist", "declined", ""].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} className={`rounded-full border px-4 py-1.5 text-sm font-medium ${status === s ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15 text-black/70"}`} data-testid={`enr-filter-${s || "all"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      <div className="rounded-lg bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/5"><tr>
            <th className="text-left p-3">Date</th><th className="text-left p-3">Guardian</th><th className="text-left p-3">Child</th>
            <th className="text-left p-3">Program</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Status</th>
          </tr></thead>
          <tbody>
            {data.map((e) => (
              <tr key={e.id} className="border-t border-black/5" data-testid={`enr-row-${e.id}`}>
                <td className="p-3 text-xs text-black/60">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="p-3">{e.guardian_name}<br /><span className="text-xs text-black/50">{e.guardian_email}</span></td>
                <td className="p-3">{e.child_first_name} <span className="text-xs text-black/50">(age {e.child_age}{e.school ? `, ${e.school}` : ""})</span></td>
                <td className="p-3 text-xs">{e.preferred_program}</td>
                <td className="p-3 text-xs">{e.preferred_contact}{e.guardian_phone ? ` · ${e.guardian_phone}` : ""}</td>
                <td className="p-3">
                  <select value={e.status} onChange={(ev) => update(e.id, ev.target.value)} className="rounded-md border border-black/10 px-2 py-1 text-xs">
                    {["new", "in_review", "enrolled", "waitlist", "declined", "closed"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && <p className="p-6 text-sm text-black/60">No enrollment inquiries{status ? ` with status "${status}"` : ""}.</p>}
      </div>
    </>
  );
}

export function PartnershipsAdmin() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-partnerships"], queryFn: adminPartnerships });
  const update = async (id, s) => {
    try { await adminUpdatePartnership(id, s); toast.success(`Marked ${s}`); qc.invalidateQueries({ queryKey: ["admin-partnerships"] }); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="rounded-lg bg-white border border-black/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-black/5"><tr>
          <th className="text-left p-3">Date</th><th className="text-left p-3">Organization</th>
          <th className="text-left p-3">Contact</th><th className="text-left p-3">Type</th>
          <th className="text-left p-3">Message</th><th className="text-left p-3">Status</th>
        </tr></thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.id} className="border-t border-black/5" data-testid={`part-row-${p.id}`}>
              <td className="p-3 text-xs text-black/60">{new Date(p.created_at).toLocaleDateString()}</td>
              <td className="p-3 font-semibold">{p.organization}</td>
              <td className="p-3">{p.contact_name}<br /><span className="text-xs text-black/50">{p.email}{p.phone ? ` · ${p.phone}` : ""}</span></td>
              <td className="p-3 text-xs">{p.partnership_type}</td>
              <td className="p-3 text-xs max-w-md">{p.message || "—"}</td>
              <td className="p-3">
                <select value={p.status} onChange={(ev) => update(p.id, ev.target.value)} className="rounded-md border border-black/10 px-2 py-1 text-xs">
                  {["new", "in_review", "approved", "active", "declined", "closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="p-6 text-sm text-black/60">No partnership requests yet.</p>}
    </div>
  );
}
